import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  number: "01",
  leftLabel: "SURFACE",
  rightLabel: "PREPARATION",
  title: "METICULOUS PREP",
  description: "A perfect finish begins long before the first coat of paint. We sand, patch, and prime every square inch of your walls to ensure a flawless, durable foundation. We treat your home's surfaces with the same care as a fine art canvas, ensuring every imperfection is addressed before we begin.",
  attribution: "— Marcus, Lead Technician"
}, {
  number: "02",
  leftLabel: "PREMIUM",
  rightLabel: "APPLICATION",
  title: "EXPERT TECHNIQUE",
  description: "Our team utilizes professional-grade sprayers and precision brushes to achieve a streak-free, uniform finish. We understand the chemistry of modern paints and apply them with the exact pressure and speed required for a professional, long-lasting result that stands up to the test of time.",
  attribution: "— Sarah, Master Painter"
}, {
  number: "03",
  leftLabel: "COLOR",
  rightLabel: "CONSULTATION",
  title: "DESIGN GUIDANCE",
  description: "Choosing the right palette is an architectural decision. We provide expert guidance on light reflection, undertones, and finish durability to ensure your space feels cohesive and intentional. We don't just paint walls; we curate the atmosphere of your living environment.",
  attribution: "— Elena, Design Lead"
}, {
  number: "04",
  leftLabel: "QUALITY",
  rightLabel: "ASSURANCE",
  title: "FINAL INSPECTION",
  description: "Every project concludes with a rigorous walk-through. We check for edge sharpness, coverage consistency, and clean lines. We do not consider the job finished until every detail meets our exacting standards. Your satisfaction is the final stroke of our process.",
  attribution: "— David, Project Manager"
}];
export default function OurSpecializedEquipment() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4930" id="our-specialized-equipment" className="relative overflow-hidden dark bg-background text-foreground">
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/painter_texture_pattern.webp)] bg-repeat bg-center" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-44 lg:py-[180px]">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <div className="font-default text-xs md:text-sm font-bold tracking-[0.3em] mb-6 md:mb-8 text-primary">
              OUR CORE STANDARDS
            </div>
            <h2 className="font-default font-bold leading-[0.9] text-foreground" style={{
            fontSize: "clamp(3rem, 8vw, 7rem)",
            letterSpacing: "-0.05em"
          }}>
              FOUR /<br />PILLARS.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:items-end">
            <p className="font-default leading-relaxed max-w-[480px] text-muted-foreground" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)"
          }}>
              These are the non-negotiable standards that define every project we undertake at Elite Brushstrokes.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 relative">
          {principles.map((principle, i) => {
          return <PrincipleItem key={principle.number} data-index={i} principle={principle} index={i} total={principles.length} />;
        })}
        </div>

        <div ref={closingRef} className={`mt-32 md:mt-44 lg:mt-52 text-center transition-all duration-1000 ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="font-default font-bold leading-[0.95] text-foreground" style={{
          fontSize: "clamp(2.25rem, 6vw, 5.5rem)",
          letterSpacing: "-0.04em"
        }}>
            NO SHORTCUTS.<br />
            NO COMPROMISE.{" "}
            <span className="text-primary">EVER.</span>
          </h3>
        </div>
      </div>
    </section>;
}
function PrincipleItem({
  principle,
  index,
  total,
  ...rest
}: {
  principle: {
    number: string;
    leftLabel: string;
    rightLabel: string;
    title: string;
    description: string;
    attribution: string;
  };
  index: number;
  total: number;
} & React.HTMLAttributes<HTMLDivElement>) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div {...rest} ref={ref} className={`relative px-0 md:px-6 lg:px-8 py-12 md:py-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 120}ms`,
    borderTop: "1px solid hsl(190 30% 24%)",
    borderRight: index < total - 1 ? "1px solid hsl(190 30% 24% / 0.5)" : "none"
  }}>
      <div className="relative mb-8 md:mb-10 leading-none">
        <span aria-hidden="true" className="absolute font-default font-bold leading-none select-none text-muted" style={{
        fontSize: "clamp(4rem, 7vw, 8rem)",
        letterSpacing: "-0.05em",
        top: "8px",
        left: "8px"
      }}>
          {principle.number}
        </span>
        <span className="relative font-default font-bold leading-none block text-primary" style={{
        fontSize: "clamp(4rem, 7vw, 8rem)",
        letterSpacing: "-0.05em",
        WebkitTextStroke: "1px hsl(190 30% 24%)"
      }}>
          {principle.number}
        </span>
      </div>

      <div className="relative mb-6 md:mb-8 pt-6">
        <div className="absolute top-0 left-0 right-0 border-t border-border" />
        <div className="flex justify-between items-center font-default text-[10px] md:text-xs font-bold tracking-[0.2em] text-muted-foreground">
          <span>{principle.leftLabel}</span>
          <span>{principle.rightLabel}</span>
        </div>
      </div>

      <h3 className="font-default font-bold mb-6 md:mb-8 leading-[0.95] text-foreground" style={{
      fontSize: "clamp(1.75rem, 2.4vw, 3rem)",
      letterSpacing: "-0.04em"
    }}>
        {principle.title}
      </h3>

      <p className="font-default leading-relaxed mb-8 text-muted-foreground" style={{
      fontSize: "16px"
    }}>
        {principle.description}
      </p>

      <p className="font-default italic text-sm text-muted-foreground">
        {principle.attribution}
      </p>
    </div>;
}